import { useState } from 'react';
import { CheckCircle, XCircle, Sparkles, ChevronDown } from 'lucide-react';
import { TextBlock } from '@/components/content/TextBlock';

interface FeedbackPanelProps {
  isCorrect: boolean;
  correctIndex: number;
  explanation?: string;
  onAskAI: () => void;
}

const letters = ['A', 'B', 'C', 'D', 'E', 'F'];

export function FeedbackPanel({ isCorrect, correctIndex, explanation, onAskAI }: FeedbackPanelProps) {
  const [showExplanation, setShowExplanation] = useState(!isCorrect);

  const explanationToggle = explanation ? (
    <div className="mt-3">
      <button
        onClick={() => setShowExplanation((v) => !v)}
        className="flex items-center gap-1.5 text-xs font-medium text-fg-subtle hover:text-fg-muted transition-colors"
      >
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showExplanation ? 'rotate-180' : ''}`} />
        {showExplanation ? "Masquer l'explication" : "Voir l'explication"}
      </button>
      {showExplanation && (
        <div className="bg-overlay-1 rounded-xl p-3 sm:p-4 mt-2 border border-line">
          <TextBlock text={explanation} className="text-xs sm:text-sm text-fg-muted" />
        </div>
      )}
    </div>
  ) : null;

  if (isCorrect) {
    return (
      <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-sm font-semibold text-green-400">Bonne réponse !</span>
        </div>
        {explanationToggle}
      </div>
    );
  }

  return (
    <div className="bg-red-500/5 border border-red-500/15 rounded-2xl p-4 sm:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-center gap-2 mb-3">
        <XCircle className="w-5 h-5 text-red-400" />
        <span className="text-sm font-semibold text-red-400">Mauvaise réponse</span>
      </div>

      <p className="text-xs sm:text-sm text-fg-subtle mb-3">
        La bonne réponse était : <span className="text-green-400 font-medium">{letters[correctIndex]}</span>
      </p>

      {explanationToggle}

      <button
        onClick={onAskAI}
        className="flex items-center gap-1.5 px-3 py-2 bg-violet-500/10 border border-violet-500/20 rounded-xl text-xs font-medium text-violet-400 hover:bg-violet-500/15 transition-colors mt-4"
      >
        <Sparkles className="w-3.5 h-3.5" />
        Demander à l'IA
      </button>
    </div>
  );
}
